import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, Box, Button, IconButton, MenuItem, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { MAKS_BAGIAN_DP, hitungDp, tanggalLokal } from '../../utils/uangMuka';
import { fetchCustomers } from './productpercabang/apiProductPercabang';

const rp = (v) => `Rp ${Math.round(Number(v) || 0).toLocaleString('id-ID')}`;

/**
 * Panel "Uang Muka (DP)" untuk kasir web: pelanggan terdaftar (wajib), uang muka tunai / non-tunai, jatuh tempo.
 * Sisa tagihan menjadi piutang pelanggan (halaman Piutang). state = { pelanggan, non:[{uuid,nominal}], tunai, jatuhTempo }.
 * Aturan: utils/uangMuka.js (sama dengan server).
 */
const DpPanel = ({ total, nonCashMethods = [], state, onChange, disabled = false, sx }) => {
  const hasil = hitungDp(total, state, nonCashMethods);
  const [opsi, setOpsi] = useState([]);
  const [cari, setCari] = useState('');
  const urutan = useRef(0);

  useEffect(() => {
    const nomor = (urutan.current += 1);
    const t = setTimeout(async () => {
      try {
        const data = await fetchCustomers(cari);
        if (nomor === urutan.current) setOpsi(Array.isArray(data) ? data : []);
      } catch {
        if (nomor === urutan.current) setOpsi([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [cari]);

  const ubahBaris = (i, perubahan) => onChange({ ...state, non: state.non.map((b, idx) => (idx === i ? { ...b, ...perubahan } : b)) });
  const sudahIsi = state.pelanggan || state.tunai || state.non.some((b) => b.uuid || b.nominal);

  return (
    <Box sx={sx}>
      <Typography variant="body2" sx={{ color: '#6b778c', mb: 1 }}>
        Pelanggan membayar sebagian dulu (uang muka); sisa dari tagihan {rp(total)} menjadi piutang yang dilunasi kemudian.
      </Typography>
      <Autocomplete
        size="small"
        options={opsi}
        value={state.pelanggan}
        disabled={disabled}
        getOptionLabel={(o) => (o ? `${o.nama}${o.telepon ? ` • ${o.telepon}` : ''}` : '')}
        isOptionEqualToValue={(a, b) => a.uuid === b.uuid}
        filterOptions={(x) => x}
        onChange={(_, v) => onChange({ ...state, pelanggan: v })}
        onInputChange={(_, v, alasan) => { if (alasan === 'input') setCari(v); }}
        noOptionsText="Pelanggan tidak ditemukan (tambah dulu di menu Customer)"
        renderInput={(params) => <TextField {...params} label="Pelanggan (wajib)" />}
        sx={{ mb: 1.5 }}
      />
      <TextField
        fullWidth
        size="small"
        type="number"
        label="Uang muka tunai"
        value={state.tunai}
        onChange={(e) => onChange({ ...state, tunai: e.target.value })}
        disabled={disabled}
        inputProps={{ min: 0 }}
        sx={{ mb: 1 }}
      />
      {state.non.map((b, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
          <TextField
            select
            size="small"
            label="Metode non-tunai"
            value={b.uuid}
            onChange={(e) => ubahBaris(i, { uuid: e.target.value })}
            disabled={disabled}
            sx={{ flex: 5 }}
          >
            {nonCashMethods.length === 0 && <MenuItem value="" disabled>Belum ada metode, hubungi admin</MenuItem>}
            {nonCashMethods.map((m) => (
              <MenuItem key={m.uuid} value={m.uuid}>{m.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            type="number"
            label="Nominal"
            value={b.nominal}
            onChange={(e) => ubahBaris(i, { nominal: e.target.value })}
            disabled={disabled}
            inputProps={{ min: 0 }}
            sx={{ flex: 4 }}
          />
          <IconButton
            size="small"
            aria-label="Hapus bagian"
            disabled={disabled || state.non.length <= 1}
            onClick={() => onChange({ ...state, non: state.non.filter((_, idx) => idx !== i) })}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      {state.non.length < MAKS_BAGIAN_DP - 1 && nonCashMethods.length > 0 && (
        <Button size="small" disabled={disabled} onClick={() => onChange({ ...state, non: [...state.non, { uuid: '', nominal: '' }] })} sx={{ mb: 1 }}>
          + Tambah metode non-tunai
        </Button>
      )}
      <TextField
        fullWidth
        size="small"
        type="date"
        label="Jatuh tempo *"
        InputLabelProps={{ shrink: true }}
        inputProps={{ min: tanggalLokal(0) }}
        required
        error={!state.jatuhTempo}
        helperText={state.jatuhTempo ? ' ' : 'Wajib diisi'}
        value={state.jatuhTempo}
        onChange={(e) => onChange({ ...state, jatuhTempo: e.target.value })}
        disabled={disabled}
        sx={{ mb: 1.5 }}
      />
      <Box sx={{ p: 1.25, bgcolor: '#f0f2f5', borderRadius: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2">Uang muka diterima</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{rp(hasil.dibayar)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2">Sisa tagihan (piutang)</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#b45309' }}>{rp(hasil.sisa)}</Typography>
        </Box>
      </Box>
      {hasil.galat && sudahIsi && (
        <Typography variant="body2" sx={{ color: '#dc2626', mt: 1 }}>{hasil.galat}</Typography>
      )}
    </Box>
  );
};

export default DpPanel;
